import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, CheckCircle2, KeyRound, Pencil, Plus, Search, Trash2, X, XCircle } from 'lucide-react';
import {
  Button, ConfirmDialog, EmptyState, ErrorCard, KpiCard, Pagination, SkeletonKpiStrip, SkeletonTable, cn,
} from '@/components/ui';
import { DataTable, Grid, PageHeader, Section } from '@/components/layout/PageLayout';
import { LicenseStatusBadge } from '@/components/tenants/LicenseStatusBadge';
import { useCreateTenant, useDeleteTenant, useTenants, useUpdateTenant } from '@/api/tenants';
import { getErrorMessage } from '@/lib/errors';
import { showToast } from '@/lib/toast';
import type { Tenant, TenantStatus } from '@/types/tenant';

const PAGE_SIZE = 10;

// ── Tenant form (full-page, mirrors PoliciesPage's list/form pattern) ──────

function TenantForm({ tenant, onDone }: { tenant?: Tenant | null; onDone: () => void }) {
  const [tenantId, setTenantId] = useState(tenant?.tenantId ?? '');
  const [name, setName] = useState(tenant?.name ?? '');
  const [url, setUrl] = useState(tenant?.url ?? '');
  const [status, setStatus] = useState<TenantStatus>(tenant?.status ?? 'ACTIVE');

  const createMutation = useCreateTenant();
  const updateMutation = useUpdateTenant(tenant?.tenantId ?? '');
  const isPending = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error ?? updateMutation.error;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (tenant) {
      updateMutation.mutate(
        { name, url, status },
        { onSuccess: () => { showToast('Tenant updated successfully'); onDone(); } },
      );
    } else {
      createMutation.mutate(
        { tenantId, name, url, status },
        { onSuccess: () => { showToast('Tenant created successfully'); onDone(); } },
      );
    }
  };

  const inputCls =
    'w-full px-3 py-2 text-sm border border-border-subtle rounded-md bg-surface-0 text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent/50 transition-colors';

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" size="sm" onClick={onDone} leadingIcon={<ArrowLeft className="h-4 w-4" />} />
          <div>
            <h1 className="text-xl font-semibold text-text-primary">{tenant ? 'Edit tenant' : 'New tenant'}</h1>
            <p className="text-sm text-text-tertiary">Client organization details</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onDone}>Cancel</Button>
          <Button type="submit" variant="primary" size="sm" loading={isPending} leadingIcon={<Building2 className="h-3.5 w-3.5" />}>
            {tenant ? 'Save changes' : 'Save tenant'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-state-danger/40 bg-state-danger/10 px-3 py-2 text-xs font-medium text-state-danger">
          {getErrorMessage(error)}
        </div>
      )}

      <div className="max-w-md rounded-lg border border-border-subtle bg-surface-1 p-5 space-y-4">
        <h2 className="text-sm font-semibold text-text-primary">Tenant details</h2>

        <div className="space-y-1">
          <label className="block text-xs font-medium text-text-secondary">Tenant ID</label>
          <input
            className={cn(inputCls, tenant && 'cursor-not-allowed bg-surface-2 text-text-tertiary')}
            placeholder="TN-0001"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            disabled={Boolean(tenant)}
            required
            autoFocus={!tenant}
          />
        </div>

        <div className="space-y-1">
          <label className="block text-xs font-medium text-text-secondary">Tenant name</label>
          <input
            className={inputCls}
            placeholder="Acme Corp"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus={Boolean(tenant)}
          />
        </div>

        <div className="space-y-1">
          <label className="block text-xs font-medium text-text-secondary">URL</label>
          <input
            type="text"
            className={inputCls}
            placeholder="acme.example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
          />
        </div>

        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-text-secondary">Status</label>
          <div className="grid grid-cols-2 gap-2">
            {([
              { value: 'ACTIVE', icon: <CheckCircle2 className="h-4 w-4 shrink-0" />, label: 'Active' },
              { value: 'SUSPENDED', icon: <XCircle className="h-4 w-4 shrink-0" />, label: 'Suspended' },
            ] as const).map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setStatus(opt.value)}
                className={cn(
                  'flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-xs font-medium transition-all',
                  status === opt.value
                    ? 'border-accent/40 bg-accent/10 text-accent'
                    : 'border-border-subtle text-text-secondary hover:border-accent/30 hover:bg-accent/5',
                )}
              >
                {opt.icon}
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </form>
  );
}

// ── Main page ────────────────────────────────────────────────────────────

export function TenantPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<'list' | 'form'>('list');
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [deletingTenant, setDeletingTenant] = useState<Tenant | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const { data, isLoading, isError, error, refetch } = useTenants(page, PAGE_SIZE, search);
  // Unfiltered meta query (capped, same convention as PoliciesPage) so KPI totals
  // reflect all tenants rather than just the current page.
  const { data: metaData, isLoading: metaLoading } = useTenants(1, 500);
  const { mutate: deleteTenant, isPending: isDeleting } = useDeleteTenant();

  const handleSearch = (v: string) => { setSearch(v); setPage(1); };

  const tenants = data?.data ?? [];
  const totalCount = data?.total ?? 0;
  const allTenants = metaData?.data ?? [];
  const activeCount = allTenants.filter((t) => t.status === 'ACTIVE').length;
  const suspendedCount = allTenants.filter((t) => t.status === 'SUSPENDED').length;
  const licensedCount = allTenants.filter((t) => t.license !== null).length;

  const openCreate = () => { setEditingTenant(null); setView('form'); };
  const openEdit = (t: Tenant, e: React.MouseEvent) => { e.stopPropagation(); setEditingTenant(t); setView('form'); };
  const closeForm = () => { setView('list'); setEditingTenant(null); };

  const handleDelete = () => {
    if (!deletingTenant) return;
    deleteTenant(deletingTenant.tenantId, {
      onSuccess: () => { showToast('Tenant deleted successfully'); setDeletingTenant(null); },
      onError: () => setDeletingTenant(null),
    });
  };

  if (view === 'form') {
    return (
      <div className="p-1">
        <TenantForm tenant={editingTenant} onDone={closeForm} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="Governance"
        title="Tenants"
        description="Client organizations provisioned on this deployment server"
        actions={
          <Button variant="primary" size="sm" leadingIcon={<Plus className="h-3.5 w-3.5" />} onClick={openCreate}>
            Add Tenant
          </Button>
        }
      />

      {metaLoading ? (
        <SkeletonKpiStrip count={4} />
      ) : (
        <Grid cols={4}>
          <KpiCard label="Total tenants" value={allTenants.length} variant="accent" icon={<Building2 className="h-3.5 w-3.5" />} />
          <KpiCard label="Active" value={activeCount} variant="positive" />
          <KpiCard label="Suspended" value={suspendedCount} variant={suspendedCount > 0 ? 'warning' : 'neutral'} />
          <KpiCard label="Licensed" value={licensedCount} variant="neutral" icon={<KeyRound className="h-3.5 w-3.5" />} />
        </Grid>
      )}

      {isError ? (
        <ErrorCard title="Failed to load tenants" message={getErrorMessage(error)} onRetry={() => void refetch()} />
      ) : (
        <Section title="All tenants">
          {/* Search bar */}
          <div className="mb-3 flex items-center gap-3">
            <div className="relative w-64">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" />
              <input
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search by tenant ID or name…"
                className="w-full rounded-md border border-border-default bg-surface-1 py-1.5 pl-8 pr-7 text-xs text-text-primary placeholder:text-text-tertiary focus:border-border-focus focus:outline-none focus:ring-2 focus:ring-border-focus/20"
              />
              {search && (
                <button
                  onClick={() => handleSearch('')}
                  aria-label="Clear search"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
            <span className="ml-auto text-[11px] tabular-nums text-text-tertiary">
              {totalCount} tenant{totalCount !== 1 ? 's' : ''}
            </span>
          </div>

          {isLoading ? (
            <SkeletonTable rows={6} />
          ) : totalCount === 0 ? (
            <EmptyState
              icon={<Building2 />}
              title={allTenants.length === 0 ? 'No tenants yet' : 'No tenants match'}
              description={allTenants.length === 0 ? 'Create your first tenant to get started' : 'Try a different search'}
              size="sm"
            />
          ) : (
            <>
              <DataTable<Tenant>
                rows={tenants}
                onRowClick={(t) => navigate(`/tenant/${t.tenantId}`)}
                columns={[
                  {
                    key: 'status',
                    label: 'Status',
                    render: (t) => {
                      const active = t.status === 'ACTIVE';
                      return (
                        <span className={cn('text-[11px] font-medium', active ? 'text-state-success' : 'text-text-tertiary')}>
                          {active ? 'Active' : 'Suspended'}
                        </span>
                      );
                    },
                  },
                  {
                    key: 'tenantId',
                    label: 'Tenant ID',
                    render: (t) => <span className="font-mono text-[11px] text-accent">{t.tenantId}</span>,
                  },
                  { key: 'name', label: 'Name', render: (t) => <span className="text-text-primary">{t.name}</span> },
                  { key: 'license', label: 'License', render: (t) => <LicenseStatusBadge status={t.license?.status ?? null} /> },
                  {
                    key: 'createdAt',
                    label: 'Created',
                    render: (t) => (
                      <span className="tabular-nums text-[11px] text-text-tertiary">{new Date(t.createdAt).toLocaleDateString()}</span>
                    ),
                  },
                  {
                    key: 'actions',
                    label: '',
                    width: '80px',
                    render: (t) => (
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => openEdit(t, e)}
                          aria-label="Edit tenant"
                          className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-text-tertiary transition-colors hover:bg-surface-2 hover:text-text-primary"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeletingTenant(t); }}
                          aria-label="Delete tenant"
                          className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-text-tertiary transition-colors hover:bg-state-danger/10 hover:text-state-danger"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ),
                  },
                ]}
              />
              <Pagination page={page} pageSize={PAGE_SIZE} total={totalCount} onPageChange={setPage} />
            </>
          )}
        </Section>
      )}

      <ConfirmDialog
        open={deletingTenant !== null}
        title={`Delete ${deletingTenant?.name ?? 'this tenant'}?`}
        description={
          deletingTenant?.license
            ? 'This tenant has an active license — remove the license first before deleting.'
            : 'This action cannot be undone.'
        }
        confirmLabel="Delete"
        destructive
        loading={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeletingTenant(null)}
      />
    </div>
  );
}
